import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { clientAuthConfig, clientTokenKey } from '../utils/clientAuthConfig';

const ClientAuthContext = createContext(null);

export function ClientAuthProvider({ children, agentSlug }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(!!agentSlug);
  const [sessionError, setSessionError] = useState(null);

  const applyClientToken = useCallback((slug) => {
    if (!slug) return;
    const token = localStorage.getItem(clientTokenKey(slug));
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }, []);

  const loadMe = useCallback(async (slug) => {
    const token = localStorage.getItem(clientTokenKey(slug));
    if (!token) {
      setClient(null);
      setSessionError(null);
      setLoading(false);
      return;
    }
    applyClientToken(slug);
    try {
      const res = await api.get('/clients/me', clientAuthConfig(slug));
      setClient(res.data.client);
      setSessionError(null);
    } catch (err) {
      const status = err?.response?.status;
      // Only clear session on auth failure — not when the API is down, rate-limited, or restarting.
      if (status === 401 || status === 403) {
        localStorage.removeItem(clientTokenKey(slug));
        delete api.defaults.headers.common.Authorization;
        setSessionError(null);
        setClient(null);
      } else {
        setSessionError('Service temporarily unavailable. Please retry in a moment.');
      }
    } finally {
      setLoading(false);
    }
  }, [applyClientToken]);

  useLayoutEffect(() => {
    applyClientToken(agentSlug);
  }, [agentSlug, applyClientToken]);

  useEffect(() => {
    if (!agentSlug) {
      setLoading(false);
      return;
    }
    applyClientToken(agentSlug);
    setLoading(true);
    loadMe(agentSlug);
  }, [agentSlug, loadMe, applyClientToken]);

  useEffect(() => {
    if (!agentSlug) return undefined;
    const refreshToken = () => applyClientToken(agentSlug);
    window.addEventListener('focus', refreshToken);
    return () => window.removeEventListener('focus', refreshToken);
  }, [agentSlug, applyClientToken]);

  const setSession = (slug, token, clientData, options = {}) => {
    localStorage.setItem(clientTokenKey(slug), token);
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    setClient(clientData);
    setSessionError(null);
    if (options.showResumeRecap && slug) {
      sessionStorage.setItem(`a4a_resume_recap_${slug}`, '1');
    }
  };

  const logout = (slug) => {
    localStorage.removeItem(clientTokenKey(slug));
    delete api.defaults.headers.common.Authorization;
    setClient(null);
    setSessionError(null);
  };

  const hasToken = !!(agentSlug && localStorage.getItem(clientTokenKey(agentSlug)));

  const reload = useCallback(() => loadMe(agentSlug), [loadMe, agentSlug]);

  // setSession/logout only close over stable setters and params, so state
  // values + agentSlug + the already-memoized reload are the real deps.
  const value = useMemo(
    () => ({ client, loading, sessionError, hasToken, setSession, logout, reload, agentSlug }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, loading, sessionError, hasToken, agentSlug, reload]
  );

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth() {
  const ctx = useContext(ClientAuthContext);
  if (!ctx) throw new Error('useClientAuth must be used within ClientAuthProvider');
  return ctx;
}
